import React, { useState, Component } from 'react';
import { Activity, ShieldCheck, Sliders, Table2, RefreshCw } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { AdminTabs } from '../shared/AdminTabs';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { Button } from '../../../components/ui/Button';
import { RulesTab, GovernanceSettingsTab, useGovernanceData, OverviewDashboard, QuestionsTab } from '../content/governance';
// ─── Types & Constants ────────────────────────────────────────────────────────
type TabId = 'overzicht' | 'vragen' | 'regels' | 'instellingen';
const TABS = [{
  id: 'overzicht',
  label: 'Overzicht',
  icon: <Activity className="w-4 h-4" data-id="element-4866" />
}, {
  id: 'vragen',
  label: 'Vragen',
  icon: <Table2 className="w-4 h-4" data-id="element-4867" />
}, {
  id: 'regels',
  label: 'Regels',
  icon: <ShieldCheck className="w-4 h-4" data-id="element-4868" />
}, {
  id: 'instellingen',
  label: 'Instellingen',
  icon: <Sliders className="w-4 h-4" data-id="element-4869" />
}] as const;
const TAB_DESCRIPTIONS: Record<TabId, string> = {
  overzicht: 'Systeemgezondheid, top blockers en module status op een oogopslag.',
  vragen: 'Eén centrale tabel met alle vragen, validatieresultaten, quality scores en seed issues.',
  regels: 'Alle validatieregels, regelboeken en drempelwaarden — de complete governance-referentie.',
  instellingen: 'Configureer kwaliteitspoorten, pipeline-fasen en beleidsregels.'
};
// ─── Health Status ────────────────────────────────────────────────────────────
const HEALTH_CONFIG = {
  pending: {
    label: 'Niet uitgevoerd',
    color: 'text-gray-600',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    dot: 'bg-gray-400'
  },
  ok: {
    label: 'Gezond',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500'
  },
  warning: {
    label: 'Aandacht vereist',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    dot: 'bg-amber-500'
  },
  error: {
    label: 'Kritieke fouten',
    color: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
    dot: 'bg-red-500'
  }
};
function getHealthStatus(fail: number, review: number, isRunning: boolean) {
  if (isRunning) return 'pending';
  if (fail > 0) return 'error';
  if (review > 0) return 'warning';
  return 'ok';
}
// ─── Main Page Component ──────────────────────────────────────────────────────
export function GovernancePage() {
  const [activeTab, setActiveTab] = useState<TabId>('overzicht');
  const [questionFilter, setQuestionFilter] = useState<string | undefined>(undefined);
  const {
    rows,
    stats,
    systemStats,
    moduleHealth,
    familyHealthByModule,
    questionTypeDistribution,
    visualTypeDistribution,
    topBlockers,
    pipelineIssueCounts,
    isRunning,
    lastRun,
    batchResult,
    revalidate
  } = useGovernanceData();
  const overallHealth = getHealthStatus(stats.fail, stats.review, isRunning);
  const hcfg = HEALTH_CONFIG[overallHealth];
  const handleNavigateToQuestion = (filter?: string) => {
    setQuestionFilter(filter);
    setActiveTab('vragen');
  };
  return <PageLayout title="Governance & Kwaliteit" subtitle="Kwaliteitsbeheer, validatie en analyse van het vragenbestand" actions={<div className="flex items-center gap-3" data-id="element-4871">
          {/* Health Status Indicator */}
          <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg border ${hcfg.border} ${hcfg.bg}`} data-id="element-4872">
            <div className={`w-2 h-2 rounded-full ${hcfg.dot} ${overallHealth !== 'pending' ? 'animate-pulse' : ''}`} data-id="element-4873" />
            <span className={`text-xs font-medium ${hcfg.color}`} data-id="element-4874">
              {hcfg.label}
            </span>
          </div>

          {/* Re-validate Button */}
          <Button variant="primary" onClick={revalidate} disabled={isRunning} data-id="element-4875">
            {isRunning ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" data-id="element-4876" /> : <RefreshCw className="w-4 h-4 mr-2" data-id="element-4877" />}
            {isRunning ? 'Valideren...' : 'Hervalideer'}
          </Button>

          <ConnectionBadge status="live" data-id="element-4878" />
        </div>} breadcrumbs={[{
    label: 'Inhoud'
  }, {
    label: 'Governance & Kwaliteit'
  }]} data-id="element-4870">
      {/* Health Banner (Mobile) */}
      <div className={`sm:hidden flex items-center justify-between gap-3 rounded-xl border ${hcfg.border} ${hcfg.bg} p-3 mb-3`} data-id="element-4879">
        <div className="flex items-center gap-2" data-id="element-4880">
          <div className={`w-2.5 h-2.5 rounded-full ${hcfg.dot} ${overallHealth !== 'pending' ? 'animate-pulse' : ''}`} data-id="element-4881" />
          <span className={`text-sm font-medium ${hcfg.color}`} data-id="element-4882">
            {hcfg.label}
          </span>
        </div>
        {lastRun && <span className="text-xs text-gray-500" data-id="element-4883">{lastRun}</span>}
      </div>

      <AdminTabs tabs={TABS} activeTab={activeTab} onTabChange={id => {
      setActiveTab(id as TabId);
      if (id !== 'vragen') setQuestionFilter(undefined);
    }} data-id="element-4884" />

      <p className="text-sm text-gray-500 mb-5" data-id="element-4885">
        {TAB_DESCRIPTIONS[activeTab]}
      </p>

      <div data-id="element-4886">
        {activeTab === 'overzicht' && <OverviewDashboard stats={stats} systemStats={systemStats} moduleHealth={moduleHealth} familyHealthByModule={familyHealthByModule} questionTypeDistribution={questionTypeDistribution} visualTypeDistribution={visualTypeDistribution} topBlockers={topBlockers} pipelineIssueCounts={pipelineIssueCounts} isRunning={isRunning} onNavigateToQuestion={handleNavigateToQuestion} data-id="element-4887" />}
        {activeTab === 'vragen' && <QuestionsTab rows={rows} batchResult={batchResult} isRunning={isRunning} initialFilter={questionFilter} data-id="element-4888" />}
        {activeTab === 'regels' && <RulesTab data-id="element-4889" />}
        {activeTab === 'instellingen' && <GovernanceSettingsTab data-id="element-4890" />}
      </div>
    </PageLayout>;
}